import { useCallback, useRef, useState } from 'react';

/**
 * Regression 28 Sep — one submit at a time on every admin create/edit form.
 *
 * A double click on Create/Save sent the request twice: operators were created
 * twice, the second airport POST hit the unique ICAO, and aircraft Save sent two
 * PATCHes. The buttons did show `loading={mutation.isPending}`, but a submit
 * first awaits `form.validateFields()`, and the mutation only becomes pending
 * after that - so a second click inside that gap passed straight through.
 *
 * `wrap(fn)` returns a handler that ignores calls while an earlier one is still
 * running (a ref, so it holds before React re-renders), and `busy` is the state
 * to drive the button's loading/disabled. `fn` should AWAIT the request
 * (`mutateAsync`), so the lock lasts until the server has answered. Errors are
 * swallowed here: a rejected validateFields is already shown on the form, and
 * every mutation reports its own failure in its onError.
 */
export function useSingleSubmit() {
  const inFlight = useRef(false);
  const [busy, setBusy] = useState(false);
  const wrap = useCallback(
    <A extends unknown[]>(fn: (...args: A) => Promise<unknown>) =>
      async (...args: A) => {
        if (inFlight.current) return;
        inFlight.current = true;
        setBusy(true);
        try {
          await fn(...args);
        } catch {
          /* reported by the form / the mutation's onError */
        } finally {
          inFlight.current = false;
          setBusy(false);
        }
      },
    [],
  );
  return { busy, wrap };
}
